import { useCallback, useEffect, useRef } from 'react';
import { getCustomerId, getProjectId } from '../../../utils/cookies';
import { fetchTrackedRequests } from '../logic/api';
import { subscribeRequestTrackingInvalidation } from '../logic/requestTrackingInvalidation';

function countActiveRequests(items: Array<{ status: string }>): number {
  return items.filter((item) => item.status === 'queued' || item.status === 'running').length;
}

export interface UseActiveRequestCountOptions {
  enabled?: boolean;
  refreshSignal?: number | string;
  /** Changes trigger an immediate refetch (e.g. project switch). */
  projectScopeKey?: string;
  /** Refetch when the browser tab/window regains focus. */
  refreshOnWindowFocus?: boolean;
  onChange: (activeCount: number) => void;
  /** Exposes refresh for context consumers (hover, explicit calls). */
  onRegisterRefresh?: (refresh: () => Promise<void>) => void;
}

/**
 * Event-driven active-request count for the header badge (no background polling).
 */
export function useActiveRequestCount({
  enabled = true,
  refreshSignal,
  projectScopeKey = '',
  refreshOnWindowFocus = true,
  onChange,
  onRegisterRefresh,
}: UseActiveRequestCountOptions): void {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const refresh = useCallback(async () => {
    const customerId = getCustomerId();
    if (!customerId) {
      onChangeRef.current(0);
      return;
    }
    const projectId = getProjectId();
    try {
      const result = await fetchTrackedRequests({
        customerId,
        projectId: projectId || undefined,
        status: 'all',
        page: 1,
        pageSize: 100,
      });
      onChangeRef.current(countActiveRequests(result.data));
    } catch {
      // Badge is non-critical; keep last known count on transient errors.
    }
  }, [projectScopeKey]);

  useEffect(() => {
    onRegisterRefresh?.(refresh);
  }, [onRegisterRefresh, refresh]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    void refresh();
  }, [enabled, refresh, projectScopeKey]);

  useEffect(() => {
    if (!enabled || refreshSignal == null) {
      return;
    }
    void refresh();
    const timer = window.setTimeout(() => {
      void refresh();
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [enabled, refreshSignal, refresh]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    return subscribeRequestTrackingInvalidation(() => {
      void refresh();
    });
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled || !refreshOnWindowFocus) {
      return;
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, refreshOnWindowFocus, refresh]);
}
